import { pointAtStation, type Alignment, type DrapeSampler } from '@/core/alignment';

import { sampleStations } from './swathGeometry';

/** How far either side of the centreline `fitSwathToSurface` looks. */
export const FIT_SWATH_SCAN_M = 200;
/** Offset step of the scan, and the margin added outside the last hit. */
export const FIT_SWATH_STEP_M = 0.5;
/** The dialog's own bound on a swath side (`refineStationVolumes`). */
const MAX_SWATH_M = 500;

export interface FittedSwath {
  /** Metres left of the centreline, rounded outward to `FIT_SWATH_STEP_M`. */
  left: number;
  right: number;
  /** How many station lines the surface was found on, of how many scanned. */
  stationsHit: number;
  stationsScanned: number;
}

/** `v` rounded UP to the next multiple of `FIT_SWATH_STEP_M`, plus one step of
 *  margin, never past the dialog's bound. A swath that ends exactly on the
 *  surface's edge clips the outermost cells the worker samples there. */
export const roundSwathOutward = (v: number): number =>
  Math.min(MAX_SWATH_M, Math.ceil(Math.max(0, v) / FIT_SWATH_STEP_M) * FIT_SWATH_STEP_M + FIT_SWATH_STEP_M);

/**
 * One station's scan line, reduced to the corridor: the sampler is asked at
 * every offset from `-n` to `+n` steps, the hits are grouped into CONTIGUOUS
 * runs, and only the run containing (or nearest to) offset 0 is returned.
 * `null` when nothing answered anywhere on the line.
 *
 * Why the nearest run and not the outermost hit: `buildSurfaceSampler` merges
 * every `<Surface>` in the layer, so a car park, a side road or a second
 * surface shipped alongside the design answers this scan too. Taking the
 * outermost hit made the fitted swath jump to that geometry (150 m for a
 * 4 m road), and because the station step is derived from the requested
 * range — `(to − from) / 200`, clamped to 1…10 m — a different range visited
 * different stations and hit or missed the stray patch, so the same dialog
 * produced a different swath each time. The corridor is the surface that
 * reaches the centreline; a run separated from it by a gap is something else.
 *
 * A hole in the TIN cuts the run only at the stations it actually spans, and
 * the caller takes the widest run over all of them, so a bridge deck or a
 * culvert does not shrink the fit.
 */
function corridorRunAt(
  sampler: DrapeSampler,
  px: number,
  py: number,
  nx: number,
  ny: number,
  n: number,
): { min: number; max: number } | null {
  let best: { min: number; max: number } | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  let runStart: number | null = null;
  let runEnd = 0;
  /** Distance from offset 0 to the run — 0 when the run straddles it. */
  const closeRun = () => {
    if (runStart == null) return;
    const dist = runStart <= 0 && runEnd >= 0 ? 0 : Math.min(Math.abs(runStart), Math.abs(runEnd));
    if (dist < bestDist) {
      bestDist = dist;
      best = { min: runStart, max: runEnd };
    }
    runStart = null;
  };
  for (let j = -n; j <= n; j++) {
    const o = j * FIT_SWATH_STEP_M;
    if (sampler(px - nx * o, py - ny * o) == null) {
      closeRun();
      continue;
    }
    if (runStart == null) runStart = o;
    runEnd = o;
  }
  closeRun();
  return best;
}

/**
 * The swath a run should use so that its corridor is the surface being
 * measured and nothing more: scan the surface's sampler along perpendiculars
 * through the alignment and take the outermost offsets it answers at.
 *
 * Only a LandXML surface has a sampler in the browser (`crestSourcesAtom`),
 * so this is the pre-run fit; after a run the worker's
 * `summary.referenceExtentLeftM/RightM` (see `fittedSwathFromExtents`) does
 * the same job for every surface type. Returns `null` when the surface is
 * never found along the range — a wrong alignment, or a surface that does
 * not reach it within `FIT_SWATH_SCAN_M`.
 *
 * The sampler resolves every `<Surface>` of the layer, which is why the scan
 * keeps only the CONTIGUOUS run of hits nearest the centreline (see
 * `corridorRunAt`) rather than the outermost hit anywhere on the scan line.
 */
export function fitSwathToSurface(
  a: Alignment,
  sampler: DrapeSampler,
  fromStation: number,
  toStation: number,
): FittedSwath | null {
  const from = Math.max(0, Math.min(fromStation, toStation));
  const to = Math.min(a.length, Math.max(fromStation, toStation));
  // ~200 stations along the range (plus every vertex), never closer than 1 m
  // and never further apart than 10 m: 200 × 800 samples is a click, not a wait.
  const step = Math.min(10, Math.max(1, (to - from) / 200));
  const stations = sampleStations(a, from, to, step);
  const n = Math.round(FIT_SWATH_SCAN_M / FIT_SWATH_STEP_M);
  let minOffset = Number.POSITIVE_INFINITY;
  let maxOffset = Number.NEGATIVE_INFINITY;
  let hit = 0;
  for (let i = 0; i < stations.length; i++) {
    const p = pointAtStation(a, stations[i]);
    // Left normal of the heading is (-ty, tx); left is a NEGATIVE offset
    // (`projectPoint`), so the point at offset o is p - n·o.
    const nx = -p.ty;
    const ny = p.tx;
    const run = corridorRunAt(sampler, p.x, p.y, nx, ny, n);
    if (!run) continue;
    hit++;
    if (run.min < minOffset) minOffset = run.min;
    if (run.max > maxOffset) maxOffset = run.max;
  }
  if (hit === 0) return null;
  return {
    left: roundSwathOutward(-minOffset),
    right: roundSwathOutward(maxOffset),
    stationsHit: hit,
    stationsScanned: stations.length,
  };
}

/**
 * The post-run fit: the reference's reach the worker measured
 * (`StationSummary.referenceExtentLeftM/RightM`), rounded outward the same
 * way. `null` when the run carries no extents (older run, or nothing covered).
 */
export function fittedSwathFromExtents(
  left: number | null | undefined,
  right: number | null | undefined,
): { left: number; right: number } | null {
  if (left == null || right == null || !Number.isFinite(left) || !Number.isFinite(right)) return null;
  return { left: roundSwathOutward(left), right: roundSwathOutward(right) };
}

/**
 * Below this share of the swath the dock points out that the swath is wider
 * than the surface being measured and offers the fitted one. Three quarters:
 * a design that fills less than that leaves a band wider than a lane empty on
 * at least one side.
 */
export const SWATH_UNDERUSED_PCT = 75;
